<template>
	<app-header-teleport>
		<div class="flex flex-1 flex-col md:flex-row">
			<div class="flex flex-1 flex-col md:flex-row ">
				<div class="flex items-center">
					<app-back-button class="mr-2" />
					<app-title :title="`#${formatOrderId(order['orderID'])}`" />
					<img v-if="order.b2bOrder" src="@/assets/images/b2b_logo.png" alt="b2b" class="w-4 h-4 mx-2" />
					<app-icon class="mr-2" :icon="order.source" />
				</div>
				<order-status-change
					:editable="false"
					class="my-1 md:my-0 mr-2"
					style="width: fit-content"
					:order="order"
				/>
				<app-button class="btn btn-success transparent" icon="print" @click="printOrder">
					<span class="ml-1">{{ $t('print_order') }}</span>
				</app-button>
				<app-button
					v-if="chatShown"
					class="btn btn-success transparent chat-order-button mt-1 md:mt-0 md:ml-2"
					:disabled="!chatReady"
					:aria-label="chatButtonLabel"
					@click="openChatPicker"
				>
					<chat-icon name="chat-outline" class="chat-order-button__icon" />
					<span class="ml-1">{{ $t('chat.order_button') }}</span>
					<span v-show="orderChatUnread > 0" class="chat-order-button__dot" />
				</app-button>
			</div>
			<span class="text-xl font-semibold mr-2 align-middle">
				{{ $filters.formatDate(order['registrationtime']) }}
			</span>
		</div>
	</app-header-teleport>

	<div class="mt-4 lg:mt-0">
		<app-section class="mt-4">
			<div class="flex items-center gap-2 mx-1">
				<div
					v-if="order.extraTime"
					class="flex items-center btn btn-primary cursor-pointer rounded-md px-4 gap-2"
					style="width: fit-content"
					@click="showDelayModal = true"
				>
					<app-icon icon="attention" class="text-white animate-pulse" />
					<span class="text-white animate-pulse">Delay: {{ order.extraTime }}m!</span>
				</div>
			</div>
			<order-timeline :order="order" />
		</app-section>
		<div class="flex flex-col sm:flex-row my-4">
			<div class="w-full flex flex-col sm:w-2/3 mr-2">
				<orders-product class="card-details " :order="order" @updateOrderData="updateOrderData" />
				<orders-payment class="card-details " :order="order" />
				<app-section v-if="order.thirdParty && order.thirdParty.syncOrderType">
					<div class="flex flex-1 place-items-center justify-between h-10">
						<div class="flex flex-1 items-center justify-between">
							<a
								v-if="fiscalizationData.url"
								target="_blank"
								:href="fiscalizationData.url"
								class="text-info hover:underline cursor-pointer"
							>
								{{ $t('fiscalInvoice') }}
								<app-icon icon="logout" class="p-1 bg-white text-info rounded-md cursor-pointer" />
							</a>
							<span
								v-else-if="!fiscalizationData.url && order.status === 'PENDING'"
								class="text-secondary"
							>
								{{ $t('no_fiscal_invoice') }} {{ $t('no_fiscal_invoice_on_pending') }}
							</span>
							<span v-else class="text-secondary"> {{ $t('no_fiscal_invoice') }} </span>
							<app-confirmation
								:title="$t('are_you_sure')"
								@confirm="synchronizeManualOrder"
								v-if="
									order.restaurant?.businessExtraData?.syncOrderProcessingType === 1 &&
										!fiscalizationData.url &&
										order.status !== 'PENDING'
								"
							>
								<app-button :loading="loadingGenerate" class="btn btn-success transparent">
									{{ $t('generate') }} {{ $t('fiscalInvoice') }}
								</app-button>
							</app-confirmation>
						</div>
						<div v-if="fiscalizationData.url && fiscalizationData.number" class="flex flex-1 justify-end">
							<app-button
								class="btn btn-success transparent"
								icon="print"
								@click="printFiscalInvoice"
								:loading="loadingPrintFiscalInvoice"
							>
								<span class="ml-1">{{ $t('print_fiscal_invoice') }}</span>
							</app-button>
						</div>
					</div>
				</app-section>
				<order-comments class="card-details flex-1" :orderId="order['orderID']" />
			</div>
			<div class="w-full sm:w-1/3 md:ml-2 mt-4 md:mt-0">
				<app-section v-if="order.enableCookingTime && !order.b2bOrder">
					<small-order-details :order="order" />
				</app-section>
				<restaurant-details class="card-details " :restaurant="order.restaurant" />
				<customer-details v-if="!order.b2bOrder" class="card-details" :customer="order.customer" />
				<shipping-address-details
					class="card-details"
					:address="{ address: order.address, details: order['addressDetails'] }"
					:order="order"
					@onAddressEdit="editAddress"
				/>
				<map-details
					class="card-details"
					v-if="order.orderID"
					:coordinates="[order.latitude, order.longitude]"
				/>
			</div>
		</div>
	</div>
	<address-management-modal
		v-if="showEditAddressModal"
		:show="showEditAddressModal"
		@close="showEditAddressModal = false"
		:order="order"
		@addressUpdate="addressUpdate(order)"
	/>
	<extra-delivery-time-modal
		v-if="showDelayModal && canEditExtraDeliveryTime"
		:business="order.restaurant"
		@close="showDelayModal = false"
	/>
	<app-modal
		v-if="printFiscalInvoiceModal"
		@close="printFiscalInvoiceModal = !printFiscalInvoiceModal"
		:title="$t('print_fiscal_invoice')"
	>
		<div class="flex flex-col items-center mx-2 gap-6">
			<span>{{ $t('ask_to_print_fiscal_invoice') }}</span>
			<div class="flex gap-2">
				<app-button class="btn btn-success w-20" @click="printFiscalInvoice">{{ $t('yes') }}</app-button>
				<app-button class="btn btn-primary w-20" @click="printFiscalInvoiceModal = !printFiscalInvoiceModal">{{
					$t('no')
				}}</app-button>
			</div>
		</div>
	</app-modal>
	<chat-channel-picker v-if="showChatPicker" :order="order" @close="showChatPicker = false" />
</template>

<script>
	import AppTitle from '@/components/app/AppTitle';
	import OrderService from '@/services/OrderService';
	import AppIcon from '@/components/app/AppIcon';
	import OrderTimeline from '@/components/orders/details/OrderTimeline';
	import RestaurantDetails from '@/components/orders/details/RestaurantDetails';
	import CustomerDetails from '@/components/orders/details/CustomerDetails';
	import OrdersProduct from '@/components/orders/details/OrdersProduct';
	import ShippingAddressDetails from '@/components/orders/details/ShippingAddressDetails';
	import MapDetails from '@/components/orders/details/MapDetails';
	import OrdersPayment from '@/components/orders/details/OrdersPayment';
	import OrderComments from '@/components/orders/details/OrderComments';
	import AddressManagementModal from '@/components/orders/AddressManagementModal';
	import OrderStatusChange from '@/components/orders/details/OrderStatusChange';
	import UtilityService from '@/services/UtilityService';
	import ExtraDeliveryTimeModal from '@/components/ExtraDeliveryTimeModal.vue';
	import BusinessService from '@/services/sales/BusinessService';
	import AppConfirmation from '@/components/app/AppConfirmation.vue';
	import AppSection from '@/components/app/AppSection.vue';
	import AppModal from '@/components/app/AppModal.vue';
	import AppButton from '@/components/app/AppButton.vue';
	import ChatIcon from '@/components/chat/ChatIcon.vue';
	import SmallOrderDetails from '@/components/orders/SmallOrderDetails.vue';
	import ChatChannelPicker from '@/components/chat/ChatChannelPicker.vue';
	import { chatPhase, chatsForOrder, chatStore, setViewedOrderCode, toggleDrawer } from '@/services/chat/chatStore';

	export default {
		name: 'OrderDetails',
		components: {
			ChatChannelPicker,
			ChatIcon,
			SmallOrderDetails,
			AppButton,
			AppModal,
			AppSection,
			AppConfirmation,
			ExtraDeliveryTimeModal,
			AddressManagementModal,
			AppTitle,
			AppIcon,
			OrderTimeline,
			RestaurantDetails,
			CustomerDetails,
			OrdersProduct,
			ShippingAddressDetails,
			MapDetails,
			OrdersPayment,
			OrderComments,
			OrderStatusChange,
		},
		computed: {
			canEditExtraDeliveryTime() {
				return BusinessService.canEditExtraDeliveryTime();
			},
			chatShown() {
				return chatPhase() !== 'off';
			},
			chatReady() {
				return this.chatShown && !!this.order.codeId;
			},
			chatPickerTarget() {
				return this.chatReady && chatStore.businessCodes ? this.order.codeId : null;
			},
			orderChatUnread() {
				return this.chatReady
					? chatsForOrder(this.order.codeId).reduce((total, chat) => total + chat.unreadCount, 0)
					: 0;
			},
			chatButtonLabel() {
				const label = this.$t('chat.order_chat');
				return this.orderChatUnread > 0
					? `${label}, ${this.$t('chat.list_unread', { count: this.orderChatUnread })}`
					: label;
			},
		},
		data: () => ({
			loadingStatus: false,
			shouldReload: false,
			orderId: null,
			order: {
				orderID: 0,
				restaurant: {
					businessExtraData: {
						syncOrderProcessingType: 0,
					},
				},
				customer: {},
				courierUser: {},
				address: {},
				items: [],
				statusLogs: [],
				status: 'PENDING',
				thirdParty: {},
			},
			orders: [],
			showEditAddressModal: false,
			printingModal: false,
			showDelayModal: false,
			fiscalizationData: {
				number: null,
				url: null,
			},
			loadingGenerate: false,
			printFiscalInvoiceModal: false,
			loadingPrintFiscalInvoice: false,
			showChatPicker: false,
		}),
		methods: {
			getOrder(id = this.orderId) {
				this.loadingStatus = true;
				OrderService.getOrder(UtilityService.decrypt(id))
					.then((order) => {
						if (id !== this.orderId) {
							return;
						}
						this.order = order;
						if (
							order.thirdParty &&
							order.thirdParty.syncFiscalizationUrl &&
							order.thirdParty.syncFiscalizationNumber
						) {
							this.fiscalizationData.number = order.thirdParty.syncFiscalizationNumber;
							this.fiscalizationData.url = order.thirdParty.syncFiscalizationUrl;
						}
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						if (id === this.orderId) {
							this.emitter.emit('isLoading', false);
							this.loadingStatus = false;
						}
					});
			},
			synchronizeManualOrder() {
				this.loadingGenerate = true;
				OrderService.synchronizeManualOrder(this.order.orderID)
					.then((res) => {
						if (res.verificationUrl) {
							this.fiscalizationData.url = res.verificationUrl;
							this.fiscalizationData.number = res.fiscNumber;
							this.$notificationService.success('Fiscal invoice generated successfully');
						} else {
							this.$notificationService.error(res.notes || 'Failed to generate fiscal invoice');
						}
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.loadingGenerate = false;
						this.printFiscalInvoiceModal = true;
					});
			},
			updateOrderData() {
				this.getOrder();
				this.shouldReload = !this.shouldReload;
			},
			editAddress() {
				this.showEditAddressModal = true;
			},
			addressUpdate() {
				//ToDo request backend to get the new ORDER on address update and bind it directly
				this.getOrder();
				this.showEditAddressModal = false;
			},
			updateAddress(coordinates) {
				const currentAddress = this.order.customerAddress;
				currentAddress.addressDetails = this.order.addressDetails;
				OrderService.getAddressFromCoords(coordinates.lat, coordinates.lng).then(({ street }) => {
					currentAddress.addressStreet = street;
					currentAddress.latitude = coordinates.lat;
					currentAddress.longitude = coordinates.lng;
					OrderService.updateAddressAndOrderAddress(
						this.order.orderID,
						this.order.customer.id,
						currentAddress
					).then(() => {
						this.getOrder();
					});
				});
			},
			printOrder() {
				this.emitter.emit('printOrder', this.order);
			},
			printFiscalInvoice() {
				this.loadingPrintFiscalInvoice = true;
				OrderService.printFiscalInvoice({ orderId: this.order.orderID })
					.then(() => {
						this.$notificationService.info('Fiscal Invoice is sent to the printer.');
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.loadingPrintFiscalInvoice = false;
						this.printFiscalInvoiceModal = false;
					});
			},
			formatOrderId(id) {
				return OrderService.formatOrderId(id);
			},
			openChatPicker() {
				if (chatStore.businessCodes) {
					this.showChatPicker = true;
				} else if (chatPhase() === 'needsLogin') {
					toggleDrawer(true);
				} else {
					this.$notificationService.error(this.$t('chat.unavailable'));
				}
			},
		},
		mounted() {
			this.orderId = this.$route.params.id;
			this.emitter.emit('isLoading', true);
			this.getOrder();
		},
		beforeUnmount() {
			setViewedOrderCode(null);
		},
		watch: {
			'$route.params.id'(id) {
				if (id) {
					Object.assign(this.$data, this.$options.data());
					this.orderId = id;
					this.emitter.emit('isLoading', true);
					this.getOrder();
				}
			},
			'order.codeId'(codeId) {
				setViewedOrderCode(codeId);
			},
			chatPickerTarget() {
				this.showChatPicker = false;
			},
		},
	};
</script>

<style lang="scss" scoped>
	.chat-order-button {
		position: relative;
	}

	.chat-order-button__icon {
		width: 18px;
		height: 18px;
		fill: currentColor;
	}

	.chat-order-button__dot {
		position: absolute;
		top: 2px;
		right: 2px;
		width: 10px;
		height: 10px;
		background: var(--primary);
		border: 2px solid var(--white);
		border-radius: 9999px;
	}
</style>
